import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Clock, Filter as FilterIcon, RefreshCw, Users, LogIn, LogOut } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import Pagination from '@/components/Pagination';
import DataTable from '@/components/DataTable';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';
import { format } from 'date-fns';
import {
  getAttendances,
  getActiveAttendances,
  getTodayAttendances,
  exportAttendances,
} from '@/services/attendance.service';
import { fetchEmployees } from '@/pages/parametrizacion/employees/Services/employees.services';
import CheckInModal from './components/CheckInModal';
import CheckOutModal from './components/CheckOutModal';

const ITEMS_PER_PAGE = 10;

const formatHoursWorked = (ms) => {
  if (ms == null || ms < 0) return '-';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
};

const formatHora = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return format(d, 'HH:mm');
};

const formatFecha = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return format(d, 'dd/MM/yyyy');
};

// Construye los defaults: rango desde ayer 00:00 hasta hoy 23:59
const buildDefaultFilters = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return {
    fecha: { $gte: yesterday, $lte: today },
    estado: '',
    search: '',
  };
};

const AsistenciaPage = () => {
  const { toast } = useToast();

  // Filtros (formato compatible con FilterDrawer: fechas como { $gte, $lte })
  const [filters, setFilters] = useState(() => buildDefaultFilters());
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    active: 0,
    todayTotal: 0,
    todayClosed: 0,
    activeEmployees: 0,
  });
  const [loadingStats, setLoadingStats] = useState(false);

  // Drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Modales
  const [isCheckInOpen, setIsCheckInOpen] = useState(false);
  const [isCheckOutOpen, setIsCheckOutOpen] = useState(false);

  // Columnas del FilterDrawer (estilo HospedajesPage)
  const filterColumns = useMemo(
    () => [
      { id: 'fecha', label: 'Rango de fecha', type: 'daterange' },
      { id: 'estado', label: 'Estado', type: 'select', options: [
        { value: 'en_turno', label: 'En turno' },
        { value: 'cerrada', label: 'Cerrada' },
      ]},
      { id: 'search', label: 'Buscar (cédula o nombre)', type: 'text' },
    ],
    []
  );

  // Convierte los filtros a la query que espera el backend
  const buildQueryForBackend = useCallback((f) => {
    const q = {};
    if (f.fecha?.$gte) q.fechaInicio = format(f.fecha.$gte, 'yyyy-MM-dd');
    if (f.fecha?.$lte) q.fechaFin = format(f.fecha.$lte, 'yyyy-MM-dd');
    if (f.estado) q.estado = f.estado;
    if (f.search && f.search.trim()) q.search = f.search.trim();
    return q;
  }, []);

  // Cargar tabla
  const fetchAndSetAttendances = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = buildQueryForBackend(filters);
      const response = await getAttendances({
        limit: ITEMS_PER_PAGE,
        offset: currentPage,
        query,
      });
      setData(Array.isArray(response.data) ? response.data : []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al obtener marcaciones:', error);
      setData([]);
      setTotalRecords(0);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las marcaciones.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [filters, currentPage, toast, buildQueryForBackend]);

  // Cargar stats (contadores)
  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const [active, today, employees] = await Promise.all([
        getActiveAttendances().catch(() => []),
        getTodayAttendances().catch(() => []),
        fetchEmployees({ limit: 1, offset: 1, query: { estado: 'Activo' } }).catch(() => ({ totalRecords: 0 })),
      ]);

      setStats({
        active: Array.isArray(active) ? active.length : 0,
        todayTotal: Array.isArray(today) ? today.length : 0,
        todayClosed: Array.isArray(today) ? today.filter((m) => m.horaSalida).length : 0,
        activeEmployees: employees?.totalRecords || 0,
      });
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    fetchAndSetAttendances();
  }, [fetchAndSetAttendances]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Filtros
  const handleApplyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(buildDefaultFilters());
    setCurrentPage(1);
  }, []);

  const handleRemoveFilter = useCallback((filterKey) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (filterKey === 'fecha') {
        next.fecha = undefined;
      } else {
        delete next[filterKey];
      }
      return next;
    });
    setCurrentPage(1);
  }, []);

  const handleCloseDrawer = useCallback(() => setIsDrawerOpen(false), []);

  // (El botón "Limpiar" del FilterDrawer resetea el form localmente;
  // para limpiar filtros aplicados en el padre, el usuario usa la X
  // de cada chip en AppliedFilters.)

  const handleRefresh = useCallback(() => {
    fetchAndSetAttendances();
    loadStats();
  }, [fetchAndSetAttendances, loadStats]);

  const handleMarcacionRegistrada = useCallback(() => {
    handleRefresh();
  }, [handleRefresh]);

  // Columnas para la DataTable
  const columns = useMemo(
    () => [
      {
        id: 'empleado',
        label: 'Empleado',
        sortable: false,
        render: (row) => {
          const emp = row.employee || {};
          return (
            <div>
              <div className="font-medium text-gray-900">
                {emp.nombre} {emp.apellido}
              </div>
              <div className="text-xs text-gray-500">CC {emp.cedula}</div>
            </div>
          );
        },
      },
      {
        id: 'fecha',
        label: 'Fecha',
        sortable: false,
        render: (row) => (
          <span className="text-gray-700">{formatFecha(row.fecha || row.createdAt)}</span>
        ),
      },
      {
        id: 'horaEntrada',
        label: 'Entrada',
        sortable: false,
        render: (row) => (
          <div className="text-center text-gray-700">{formatHora(row.horaEntrada)}</div>
        ),
      },
      {
        id: 'horaSalida',
        label: 'Salida',
        sortable: false,
        render: (row) => (
          <div className="text-center text-gray-700">{formatHora(row.horaSalida)}</div>
        ),
      },
      {
        id: 'horasTrabajadas',
        label: 'Horas trabajadas',
        sortable: false,
        render: (row) =>
          row.horaSalida ? (
            <div className="text-center font-semibold text-gray-900">
              {formatHoursWorked(row.horasTrabajadasMs ?? row.minutosTrabajados * 60000)}
            </div>
          ) : (
            <div className="text-center text-gray-400">-</div>
          ),
      },
      {
        id: 'estado',
        label: 'Estado',
        sortable: false,
        render: (row) =>
          row.horaSalida ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Cerrada
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
              En turno
            </span>
          ),
      },
      {
        id: 'fotos',
        label: 'Fotos',
        sortable: false,
        render: (row) => (
          <div className="text-center space-x-2">
            {row.fotoEntradaUrl ? (
              <a
                href={row.fotoEntradaUrl}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline text-xs"
              >
                Entrada
              </a>
            ) : (
              <span className="text-xs text-gray-400">-</span>
            )}
            {row.fotoSalidaUrl ? (
              <a
                href={row.fotoSalidaUrl}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline text-xs"
              >
                Salida
              </a>
            ) : null}
          </div>
        ),
      },
    ],
    []
  );

  // Columnas para el Excel
  const exportColumns = useMemo(
    () => [
      { key: 'employee.cedula', header: 'Cédula' },
      { key: 'employee.nombre', header: 'Nombre' },
      { key: 'employee.apellido', header: 'Apellido' },
      { key: 'employee.rol', header: 'Rol' },
      { key: 'fecha', header: 'Fecha', formatDate: true },
      { key: 'horaEntrada', header: 'Hora de entrada' },
      { key: 'horaSalida', header: 'Hora de salida' },
      {
        key: 'horasTrabajadasMs',
        header: 'Horas trabajadas',
        transform: (v, row) => (row.horaSalida ? formatHoursWorked(v ?? row.minutosTrabajados * 60000) : '-'),
      },
      {
        key: 'estado',
        header: 'Estado',
        transform: (v, row) => (row.horaSalida ? 'Cerrada' : 'En turno'),
      },
      { key: 'fotoEntradaUrl', header: 'Foto entrada' },
      { key: 'fotoSalidaUrl', header: 'Foto salida' },
      { key: 'observaciones', header: 'Observaciones' },
    ],
    []
  );

  return (
    <div className="container mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        {/* Header con título */}
        <div className="flex items-center space-x-2">
          <Clock className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Asistencia de Personal</h1>
            <p className="text-muted-foreground">
              Marcación de entrada y salida con evidencia fotográfica
            </p>
          </div>
        </div>

        {/* Contadores */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <p className="text-sm font-medium text-gray-600 flex items-center gap-1">
              <Users className="h-3 w-3" />
              Personal activo
            </p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{stats.activeEmployees}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm font-medium text-orange-600">En turno ahora</p>
            <p className="text-3xl font-bold text-orange-900 mt-2">{stats.active}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm font-medium text-blue-600">Marcaciones hoy</p>
            <p className="text-3xl font-bold text-blue-900 mt-2">{stats.todayTotal}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm font-medium text-green-600">Cerradas hoy</p>
            <p className="text-3xl font-bold text-green-900 mt-2">{stats.todayClosed}</p>
          </Card>
        </div>

        {/* Acciones de marcación */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Button
            size="lg"
            onClick={() => setIsCheckInOpen(true)}
            className="h-16 bg-green-600 hover:bg-green-700 gap-3 text-base"
          >
            <LogIn className="h-6 w-6" />
            <div className="flex flex-col items-start">
              <span className="font-semibold">Marcar Entrada</span>
              <span className="text-xs font-normal text-green-100">Personal que recién llega</span>
            </div>
          </Button>
          <Button
            size="lg"
            onClick={() => setIsCheckOutOpen(true)}
            className="h-16 bg-orange-600 hover:bg-orange-700 gap-3 text-base"
          >
            <LogOut className="h-6 w-6" />
            <div className="flex flex-col items-start">
              <span className="font-semibold">Marcar Salida</span>
              <span className="text-xs font-normal text-orange-100">Personal en turno</span>
            </div>
          </Button>
        </div>

        {/* Tabla de marcaciones */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <CardTitle>Registro de marcaciones</CardTitle>
                <CardDescription>
                  Lista de todas las marcaciones con horas trabajadas
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsDrawerOpen(true)}
                  className="flex items-center space-x-2"
                >
                  <FilterIcon className="h-4 w-4" />
                  <span>Filtros</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={isLoading || loadingStats}
                  className="flex items-center space-x-2"
                >
                  <RefreshCw className={`h-4 w-4 ${(isLoading || loadingStats) ? 'animate-spin' : ''}`} />
                  <span>Actualizar</span>
                </Button>
                <ExportExcelButton
                  service={exportAttendances}
                  filters={filters}
                  columns={exportColumns}
                  fileName="asistencia_personal"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <AppliedFilters
                filters={filters}
                fields={filterColumns}
                onRemoveFilter={handleRemoveFilter}
              />

              <DataTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                emptyMessage="No hay marcaciones que coincidan con los filtros aplicados."
              />

              {totalRecords > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalRecords}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        fields={filterColumns}
        onApplyFilters={handleApplyFilters}
        initialFilters={filters}
      />

      {/* Modales de marcación */}
      <CheckInModal
        isOpen={isCheckInOpen}
        onClose={() => setIsCheckInOpen(false)}
        onSuccess={handleMarcacionRegistrada}
      />
      <CheckOutModal
        isOpen={isCheckOutOpen}
        onClose={() => setIsCheckOutOpen(false)}
        onSuccess={handleMarcacionRegistrada}
      />
    </div>
  );
};

export default AsistenciaPage;
